<script lang="ts">
import { defineComponent, ref, watch, type PropType } from 'vue';
import type {
  UIEnigmaCodeState,
  UIMastermindState,
  UITurnEconomyState,
} from '@legendary-arena/game-engine';
import { useTurnActions, healLockGate } from '../../composables/useTurnActions';
import { useCardCostGating, type GatingResult } from '../../composables/useCardCostGating';
import CardTile from './CardTile.vue';
import DarkPortalMarker from './DarkPortalMarker.vue';
import CrossedSwordsIcon from './CrossedSwordsIcon.vue';
import EnigmaGuessPicker from './EnigmaGuessPicker.vue';
import type { SubmitMove } from './uiMoveName.types';

/**
 * Mastermind tile — renders the mastermind id + tactics-remaining counter
 * + WP-128 `attachedBystanders` array. Click fires `fightMastermind`.
 *
 * `mastermind.attachedBystanders` (D-12805 Interpretation B) is bystanders
 * captured by the mastermind itself. Per D-24654 a Master Strike captures
 * none; the store fills from a Villain-Deck Bystander revealed while the City
 * is empty, and from the no-Villain capture fallback (Here, Hold This, D-24500;
 * count-scaled captures, D-24537). Per WP-505 it renders as a count-only "N captured"
 * badge (face-down = identity hidden), matching the city-villain badge; these
 * are the mastermind's own captures, never the top-level city-villain
 * `G.attachedBystanders` (which render on the city row).
 *
 * `mastermind.hypnoThralls` (WP-399 / D-24202) is the non-grey Heroes co2e
 * Loki's Master Strike stacks next to Loki (the D-24201 zone). Unlike the
 * face-down bystanders, Thralls are face-up Heroes, so they render by
 * name/image via CardTile, shown only when non-empty.
 *
 * Cost gating: the mastermind is fightable when `economy.availableAttack
 * >= mastermind.fightCost` — the engine's projected fight cost (WP-750 /
 * D-24574), falling back to the printed `display.cost` only for snapshots that
 * predate the field. A `Fight N` badge shows the projected cost whenever it
 * differs from the printed one. Disabled-state tooltip precedence locked at
 * EC-132 §3 (stage → resource → structural; "all tactics defeated" is the
 * structural lock).
 *
 * Per the EC-132 §2 SFC authoring whitelist: this is a tested non-leaf
 * composer that USES composables, so it MUST use
 * `defineComponent({ setup() { return {...} } })` per P6-30 / P6-46 /
 * D-6512.
 *
 * @see WP-129 §Acceptance Criteria — Mastermind tile
 * @see DECISIONS.md D-12805 attachedBystanders semantics
 * @see DECISIONS.md D-12806 safe-skip resolution
 */
export default defineComponent({
  name: 'MastermindTile',
  components: { CardTile, DarkPortalMarker, CrossedSwordsIcon, EnigmaGuessPicker },
  emits: ['read'],
  props: {
    mastermind: {
      type: Object as PropType<UIMastermindState>,
      required: true,
    },
    // why: WP-727 / D-24548 — the Portals Dark Portal above the Mastermind, as a
    // +N attack. Derived by the parent from scheme.darkPortals (0 = no portal /
    // non-Portals scheme); the marker renders only when it is > 0.
    darkPortalBonus: {
      type: Number,
      required: false,
      default: 0,
    },
    currentStage: {
      type: String,
      required: true,
    },
    isViewerTurn: {
      type: Boolean,
      required: false,
      default: true,
    },
    // why: D-24614 — UIState.game.hasHealedThisTurn. The engine refuses
    // recruit and fight for the rest of a turn in which the viewer healed
    // (D-24180 heal lock), so the button gate must see it too.
    hasHealedThisTurn: {
      type: Boolean,
      required: false,
      default: false,
    },
    economy: {
      type: Object as PropType<UITurnEconomyState>,
      required: true,
    },
    submitMove: {
      type: Function as PropType<SubmitMove>,
      required: true,
    },
    // why: the WP-732 / D-24553 "finish your turn" banner is only true while the
    // match is still live — once the game is over there is no turn left to finish.
    isGameOver: {
      type: Boolean,
      required: false,
      default: false,
    },
    // why: WP-830 / D-24763 — The Unbreakable Enigma Code (scheme.enigmaCode). While it holds a
    // card, a Mastermind fight starts with a guess, so both Fight buttons open the guess picker.
    enigmaCode: {
      type: Object as PropType<UIEnigmaCodeState | undefined>,
      required: false,
      default: undefined,
    },
    // why: WP-854 / D-24790 — the band stand-up of the Mastermind card. Only
    // PlayDesktop sets it (from useBoardTilt's lift gate); every other use,
    // PlayMobile included, keeps it false and renders the tile as before.
    isTiltLiftEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    // why: WP-830 / D-24763 — null while no guess is being made; otherwise whether the fight the
    // guess starts uses Excessive Violence.
    const enigmaGuessFight = ref<null | { useExcessiveViolence: boolean }>(null);

    /**
     * Whether a Mastermind fight needs an Enigma Code guess first.
     *
     * @returns true while the code holds a card.
     */
    function needsEnigmaGuess(): boolean {
      return (props.enigmaCode?.cards.length ?? 0) > 0;
    }

    /**
     * The picker's identity: it changes whenever the code this viewer sees changes.
     *
     * @returns A key built from each card's visible state.
     */
    function enigmaPickerKey(): string {
      // why: WP-830 / D-24763 — a mid-turn Twist or a mix changes the code under an open picker;
      // a new key remounts it, so it never submits colors for the old positions.
      const parts: string[] = [String(props.enigmaCode?.isRevealed === true)];
      for (const card of props.enigmaCode?.cards ?? []) {
        parts.push(`${card.display?.extId ?? '?'}:${card.peekedBy.join(',')}`);
      }
      return parts.join('|');
    }

    /**
     * Submits the fight with the guess the picker made.
     *
     * @param guess - One color per Enigma card, in code order.
     */
    function onEnigmaGuess(guess: string[]): void {
      const useExcessiveViolence = enigmaGuessFight.value?.useExcessiveViolence === true;
      enigmaGuessFight.value = null;
      // why: the client submits INTENT; the engine reveals the code and decides.
      props.submitMove('fightMastermind', useExcessiveViolence ? { enigmaGuess: guess, useExcessiveViolence: true } : { enigmaGuess: guess });
    }

    function mastermindFightCost(): number | null {
      // why: WP-750 / D-24574 — the engine's projected cost
      // (resolveMastermindFightCost: printed + the Portals Dark-Portal bonus) is
      // the only fight-cost source. The `display.cost` fallback covers only
      // snapshots from before `fightCost` existed; it never adds a term itself.
      const mastermindFightCost = props.mastermind.fightCost ?? props.mastermind.display.cost;
      return mastermindFightCost;
    }

    function isFightCostUnaffordable(): boolean {
      // why (Jeff feedback): loud only when the badge explains a disabled Fight —
      // the viewer's Main stage and the projected cost is more than their attack.
      const stage = useTurnActions(props.currentStage, props.isViewerTurn).canFightMastermind();
      if (!stage.allowed) {
        return false;
      }
      // why: D-24614 — after a heal the Fight is disabled by the heal lock, not
      // by cost, so the badge must not go loud and blame the attack total.
      if (!healLockGate(props.hasHealedThisTurn).allowed) {
        return false;
      }
      return !useCardCostGating(props.economy).canFight(mastermindFightCost(), 'mastermind').allowed;
    }

    function hasFightCostBadge(): boolean {
      // why: WP-750 / D-24574 — players see the number the engine will actually
      // charge, only when it differs from the printed cost. Never rendered from the
      // fallback: an absent fightCost (an old snapshot or fixture) shows no badge.
      if (props.mastermind.fightCost === undefined) {
        return false;
      }
      return props.mastermind.fightCost !== props.mastermind.display.cost;
    }

    function gateForFight(): GatingResult {
      // why: disabled-state tooltip precedence per EC-132 §3 — stage →
      // resource → structural. Stage gate first; then cost gate (consumes
      // WP-128 economy.availableAttack); finally the structural-lock
      // "all tactics defeated" check.
      const stage = useTurnActions(props.currentStage, props.isViewerTurn).canFightMastermind();
      if (!stage.allowed) {
        return stage;
      }
      const healLock = healLockGate(props.hasHealedThisTurn);
      if (!healLock.allowed) {
        return healLock;
      }
      // why: WP-790 / D-24652 — pass the 'mastermind' target (here, in the cost badge and in the
      // EV check) so restricted attack whose grant names the Mastermind counts, exactly as the
      // fightMastermind gate does — the served figure must match the engine gate.
      const cost = useCardCostGating(props.economy).canFight(mastermindFightCost(), 'mastermind');
      if (!cost.allowed) {
        return cost;
      }
      if (props.mastermind.tacticsRemaining === 0) {
        // why: WP-687 / D-24504 — under the optional Final Blow rule, an
        // all-tactics-defeated Mastermind is NOT "already fallen": it stays
        // fightable a 5th, final time (the fight that wins by putting the
        // Mastermind card into the Victory Pile). The projected finalBlowPending
        // inverts this structural lock. Precedence is preserved (RS-4): the stage
        // and cost gates above already ran, so an under-resourced final fight is
        // still blocked by cost — mirroring the engine's silent no-op.
        if (props.mastermind.finalBlowPending === true) {
          return { allowed: true, reason: null };
        }
        return {
          allowed: false,
          reason: 'All tactics defeated — the Mastermind is vanquished and victory is assured. Finish your turn, then End Turn to win.',
        };
      }
      return { allowed: true, reason: null };
    }

    // why: WP-830 / D-24763 — an open picker closes once the fight is no longer possible (the turn
    // ended or left the main stage, the Attack is gone, the game is over), so it never lingers
    // into another turn.
    watch(
      () => gateForFight().allowed && !props.isGameOver,
      (isFightPossible) => {
        if (!isFightPossible) {
          enigmaGuessFight.value = null;
        }
      },
    );

    function onFight(): void {
      if (needsEnigmaGuess()) {
        enigmaGuessFight.value = { useExcessiveViolence: false };
        return;
      }
      // why: empty-object payload — fightMastermind takes no arguments by
      // engine design. The move always defeats the top tactic.
      props.submitMove('fightMastermind', {});
    }

    function onRead(): void {
      // why: surface the full card + Master-Strike / special rules in the
      // shared CardReaderModal. `gameText` is already in the projection; the
      // tile keeps it off-board to stay compact and emits it on demand.
      emit('read', {
        title: props.mastermind.display.name,
        display: props.mastermind.display,
        gameText: props.mastermind.gameText ?? [],
      });
    }

    /**
     * Whether the Mastermind is vanquished but the match is still running — the
     * WP-732 / D-24553 window where the current player finishes their turn
     * (keeps fighting Villains / rescuing Bystanders) and the heroes win at
     * End Turn. False under a pending Final Blow (the Mastermind is still
     * fightable) and once the game is over.
     *
     * @returns true while the "victory assured — finish your turn" banner applies.
     */
    function isVictoryAssured(): boolean {
      if (props.isGameOver) {
        return false;
      }
      if (props.mastermind.tacticsRemaining !== 0) {
        return false;
      }
      return props.mastermind.finalBlowPending !== true;
    }

    function showEvFight(): boolean {
      // why: WP-738 / D-24561 — offer "Fight using Excessive Violence" against the
      // Mastermind only when the normal fight is allowed AND the player has EV
      // available (WP-739 economy.excessiveViolenceAvailable) and can afford the
      // cost + 1 overspend. The engine still validates; this only decides whether
      // to SHOW the control. Absent field = false.
      if (!gateForFight().allowed) {
        return false;
      }
      return useCardCostGating(props.economy).canFightWithExcessiveViolence(mastermindFightCost(), 'mastermind');
    }

    function onFightEV(): void {
      // why: WP-738 / D-24561 — the client submits INTENT (useExcessiveViolence);
      // the engine overspends +1 and fires the enrolled EV abilities, or silently
      // fights normally if it cannot. Once-per-turn is enforced engine-side — the
      // availability field goes absent after the EV fight and this affordance
      // disappears on the next projection.
      if (needsEnigmaGuess()) {
        enigmaGuessFight.value = { useExcessiveViolence: true };
        return;
      }
      props.submitMove('fightMastermind', { useExcessiveViolence: true });
    }

    return {
      enigmaGuessFight,
      enigmaPickerKey,
      onEnigmaGuess,
      gateForFight,
      hasFightCostBadge,
      isFightCostUnaffordable,
      onFight,
      onRead,
      isVictoryAssured,
      showEvFight,
      onFightEV,
    };
  },
});
</script>

<template>
  <section
    class="mastermind-tile"
    :class="{
      'mastermind-tile--tilt-lift': isTiltLiftEnabled,
      'mastermind-tile--guessing': enigmaGuessFight !== null && enigmaCode !== undefined,
    }"
    data-testid="play-mastermind-tile"
    aria-label="Mastermind"
  >
    <!-- why: WP-854 / D-24790 — mastermind-tile--guessing mirrors the Enigma
         picker's own v-if, verbatim, so the card stays flat while the guess is
         open. The plane's rule already sits inside :has(), which cannot nest, so
         it tests this static class. -->
    <!-- why: WP-727 / D-24548 — the Portals Dark Portal above the Mastermind
         (twist 1). Shown only when darkPortalBonus > 0 (0 = no portal / a
         non-Portals scheme); the +N is prop-driven, never hardcoded. -->
    <DarkPortalMarker v-if="darkPortalBonus > 0" :attack-bonus="darkPortalBonus" />
    <!-- why: Jeff feedback (Dr. Doom / Legacy Virus 2p, turn 26) — after the 4th
         Tactic fell the only signal was a disabled Fight button + hover tooltip, so
         a player with attack left read it as "can't attack the Mastermind" and only
         learned they had won at End Turn. WP-732 / D-24553 lets the player finish
         the turn, so say so plainly and prominently. role="status" announces it. -->
    <div
      v-if="isVictoryAssured()"
      class="mastermind-vanquished"
      data-testid="play-mastermind-vanquished"
      role="status"
    >
      <strong class="mastermind-vanquished-title">🏆 Mastermind defeated — victory is assured!</strong>
      <span class="mastermind-vanquished-body">
        Finish your turn: fight Villains, rescue Bystanders, and recruit for Victory Points. Press End Turn to win.
      </span>
    </div>
    <button
      type="button"
      class="mastermind__fight-button"
      data-testid="play-mastermind-button"
      :data-mastermind-id="mastermind.id"
      :disabled="!gateForFight().allowed"
      :aria-disabled="!gateForFight().allowed ? 'true' : undefined"
      :title="gateForFight().reason ?? undefined"
      @click="onFight"
    >
      <!-- why: disabled-state tooltip precedence locked at EC-132 §3
           (stage → resource → structural). Reason text is bound from
           useTurnActions / useCardCostGating + the structural "all
           tactics defeated" override. -->
      <!-- why: WP-750 / D-24574 — the frame is the badge's positioning context, so
           the Fight N badge sits at the bottom of the CARD art. Pinning it to the
           button's bottom would cover the Tactics-remaining / Final Blow lines
           stacked under the card. -->
      <span class="mastermind__card-frame">
        <CardTile
          :display="mastermind.display"
          size="md"
          :interactive="gateForFight().allowed"
          :tooltip="gateForFight().reason"
          :show-label="true"
        />
        <!-- why: WP-750 / D-24574 — the engine's projected fight cost, shown only
             when it differs from the printed cost and never from the display.cost
             fallback. Bottom of the card, clear of the top band where the printed
             cost sits. -->
        <span
          v-if="hasFightCostBadge()"
          class="mastermind__fight-cost"
          :class="{ 'mastermind__fight-cost--unaffordable': isFightCostUnaffordable() }"
          data-testid="play-mastermind-fight-cost"
        >Fight {{ mastermind.fightCost }}</span>
      </span>
      <span class="mastermind-status" data-testid="play-mastermind-tactics-remaining">
        Tactics remaining: {{ mastermind.tacticsRemaining }}
      </span>
      <!-- why: WP-687 / D-24504 — the Final Blow affordance. When every Tactic is
           defeated but the Mastermind card is not yet won, the tile inverts its
           "already fallen" lock into a call to strike the final blow. Plain status
           copy (never raw marker syntax). -->
      <span
        v-if="mastermind.finalBlowPending"
        class="mastermind-final-blow"
        data-testid="play-mastermind-final-blow"
      >
        <CrossedSwordsIcon class="mastermind-final-blow-icon" />
        Final blow — fight the Mastermind
      </span>
    </button>
    <!-- why: WP-738 / D-24561 — the "Fight using Excessive Violence" affordance
         against the Mastermind. A SEPARATE opt-in button (the normal Fight click
         above is unchanged); shown only when the fight is allowed AND the player
         has EV available and can afford cost + 1. Submitting it sends the
         useExcessiveViolence intent; the engine decides the outcome. -->
    <button
      v-if="showEvFight()"
      type="button"
      class="mastermind-ev-fight"
      data-testid="play-mastermind-ev"
      title="Spend 1 extra attack to fire every Excessive Violence ability on cards you played this turn."
      @click="onFightEV"
    >
      <CrossedSwordsIcon class="mastermind-ev-fight-icon" />
      <span class="mastermind-ev-fight-label">Excessive Violence</span>
      <span class="mastermind-ev-fight-cost">+1</span>
    </button>
    <!-- why: WP-830 / D-24763 — the Enigma Code guess a Mastermind fight starts with.
         Jeff feedback (Loki / Enigma Code 2p, 2026-10-31): drawn inside the tile it
         was ~125×112 px on the scaled, tilted board, each card's colors wrapped two
         to a line and the sticky actions hid the "Card 2" label, so a click read as
         one card's landed on the next. It is a body-level dialog now; the Teleport
         also keeps it out of the board's scale-to-fit, so the board never rescales. -->
    <Teleport v-if="enigmaGuessFight !== null && enigmaCode !== undefined" to="body">
      <EnigmaGuessPicker
        :key="enigmaPickerKey()"
        :enigma-code="enigmaCode"
        @submit="onEnigmaGuess"
        @cancel="enigmaGuessFight = null"
      />
    </Teleport>
    <!-- why: the full card + Master-Strike / special rules open in the shared
         CardReaderModal instead of rendering inline, so the tile stays short
         (Jeff: the top row wasted vertical space without being readable). -->
    <button
      type="button"
      class="mastermind-read"
      data-testid="play-mastermind-read"
      @click="onRead"
    >
      Read card ▸
    </button>
    <!-- why: WP-505 + Jeff feedback — captured bystanders are face down, so they
         render as a compact icon + count badge (identity hidden), matching the
         city-villain badge; shown only when the mastermind holds some. The person
         glyph is decorative (aria-hidden) with the full phrase on the aria-label. -->
    <span
      v-if="mastermind.attachedBystanders.length > 0"
      class="mastermind-bystanders"
      data-testid="play-mastermind-bystanders"
      :aria-label="mastermind.attachedBystanders.length + ' bystanders captured'"
      :title="mastermind.attachedBystanders.length + ' bystander(s) captured'"
    >
      <span class="mastermind-bystanders-icon" aria-hidden="true">👤</span>{{ mastermind.attachedBystanders.length }}
    </span>
    <!-- why: WP-399 / D-24202 — Hypno-Thralls are the non-grey Heroes co2e
         Loki's Master Strike stacks next to Loki (the D-24201 zone made
         visible). Unlike the face-down captured bystanders above (a count-only
         badge), Thralls are face-up Heroes, so they render by name/image via
         CardTile. Shown only when the mastermind holds some, matching the
         bystander non-empty guard. Append order is preserved from the engine
         projection — no client-side sort. -->
    <div
      v-if="mastermind.hypnoThralls.length > 0"
      class="mastermind-thralls"
      data-testid="play-mastermind-hypno-thralls"
      :aria-label="mastermind.hypnoThralls.length + ' Hypno-Thralls stacked next to Loki'"
    >
      <span class="mastermind-thralls-label">Hypno-Thralls</span>
      <ul class="mastermind-thralls-list">
        <li
          v-for="(thrall, index) in mastermind.hypnoThralls"
          :key="thrall.extId + '-' + index"
          class="mastermind-thralls-item"
          data-testid="play-mastermind-hypno-thrall"
        >
          <CardTile :display="thrall.display" size="sm" :show-label="true" />
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped>
.mastermind-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--color-foreground, #999);
}

.mastermind-tile button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  padding: 0.25rem 0.5rem;
}

/* why: WP-750 — the Fight button and its card frame are positioning contexts;
   the frame anchors the Fight N badge to the card art so it overlays the tile
   instead of taking layout space or covering the status lines below the card. */
.mastermind__fight-button {
  position: relative;
}

.mastermind__card-frame {
  position: relative;
  display: inline-flex;
}

/* why: WP-750 / D-24574 — the projected Mastermind fight cost, pinned to the
   bottom of the Fight button. Dark pill, matching the City badge. */
.mastermind__fight-cost {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  padding: 0.05rem 0.35rem;
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  white-space: nowrap;
  pointer-events: none;
}

/* why (Jeff feedback, match 25-GJ0oXBwy): the quiet dark pill went unnoticed at the
   one moment it matters — the villain costs more than the attack in hand. On that
   state only, the badge turns red, grows slightly and pulses once; reduced motion
   keeps the colour and drops the pulse. */
.mastermind__fight-cost--unaffordable {
  background: #b91c1c;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9), 0 0 10px rgba(185, 28, 28, 0.85);
  font-size: 0.75rem;
  animation: mastermind__fight-cost-pulse 700ms ease-out 1;
}

@keyframes mastermind__fight-cost-pulse {
  from {
    transform: translateX(-50%) scale(1.35);
  }
  to {
    transform: translateX(-50%) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .mastermind__fight-cost--unaffordable {
    animation: none;
  }
}

.mastermind-id {
  font-weight: 600;
}

.mastermind-cost,
.mastermind-status {
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
}

/* why: WP-505 + Jeff feedback — compact icon + count badge (face-down
   bystanders), matching the city-villain pill: an inline-flex pill so the person
   glyph and the number sit tight together. */
.mastermind-bystanders {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.1rem;
  padding: 0.05rem 0.3rem;
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  white-space: nowrap;
}

.mastermind-bystanders-icon {
  font-size: 0.7rem;
}

.mastermind-read {
  align-self: flex-start;
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
}

/* why: WP-687 — the Final Blow call-to-action badge. Amber/gold so it reads as a
   payoff moment (the last blow that wins), distinct from the neutral status line. */
.mastermind-final-blow {
  align-self: flex-start;
  padding: 0.1rem 0.45rem;
  border-radius: 0.75rem;
  background: var(--color-warning, #b45309);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
}

/* why: WP-738 / D-24561, graphic fix-forward 2026-09-22 — the "Fight using
   Excessive Violence" opt-in against the Mastermind. A filled RED overspend action
   so it reads as a deliberate, costly extra-attack fight distinct from the normal
   Fight click — not as card ability-text (the earlier subtle outline + a
   non-rendering U+2694 glyph made operators miss it). The crossed-swords SVG and
   the "+1" cost badge spell out the extra-attack price on the button itself. */
.mastermind-ev-fight {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  align-self: flex-start;
  margin-top: 0.2rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid #7a2818;
  border-radius: 0.35rem;
  background: linear-gradient(180deg, #c8452b 0%, #a5361f 100%);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(122, 40, 24, 0.45);
}

.mastermind-ev-fight:hover {
  background: linear-gradient(180deg, #d85336 0%, #b53d24 100%);
}

.mastermind-ev-fight:active {
  transform: translateY(1px);
}

.mastermind-ev-fight-icon {
  font-size: 0.85rem;
}

/* why: the "+1" attack-cost badge — a lighter inset pill so the price of the
   overspend is unmistakable without a separate tooltip read. */
.mastermind-ev-fight-cost {
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  background: rgba(0, 0, 0, 0.28);
  font-variant-numeric: tabular-nums;
}

/* why: the Final Blow badge shares the crossed-swords SVG so the two "strike"
   affordances read consistently; it inherits the badge's amber via currentColor. */
.mastermind-final-blow-icon {
  font-size: 0.9em;
}

/* why: the victory-assured banner is the payoff moment of the match, so it uses
   the same amber/gold as the Final Blow badge but at block size — it must be
   impossible to miss next to a Fight button that is now (correctly) disabled. */
.mastermind-vanquished {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.4rem;
  background: var(--color-warning, #b45309);
  color: #fff;
  font-size: 0.78rem;
  line-height: 1.25;
}

.mastermind-vanquished-title {
  font-size: 0.85rem;
  font-weight: 800;
}

/* why: WP-399 — Hypno-Thralls are face-up Heroes stacked next to Loki, so they
   render as a small named/imaged group (contrast the face-down bystander
   count badge above). */
.mastermind-thralls {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  align-self: stretch;
}

.mastermind-thralls-label {
  font-size: 0.7rem;
  font-weight: 700;
  opacity: 0.85;
}

.mastermind-thralls-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mastermind-thralls-item {
  display: flex;
}

/* why: WP-854 / D-24790 — the Mastermind card stands up on hover, as the City
   and HQ cards do (D-24788). Pointer devices only, and only under --tilt-lift.
   Every link from the tile down to the card frame preserves 3D, or the card
   would rise but stay tilted; the links are on only while the fight button is
   hovered, because at rest coplanar preserve-3d layers z-fight in Chrome's hit
   test and steal clicks (EC-890 Amendment A). Never while the Enigma guess is
   open (--guessing). */
@media (hover: hover) {
  .mastermind-tile--tilt-lift:not(.mastermind-tile--guessing):has(.mastermind__fight-button:hover),
  .mastermind-tile--tilt-lift:not(.mastermind-tile--guessing):has(.mastermind__fight-button:hover) .mastermind__fight-button,
  .mastermind-tile--tilt-lift:not(.mastermind-tile--guessing):has(.mastermind__fight-button:hover) .mastermind__card-frame {
    transform-style: preserve-3d;
  }
  /* why: the bottom-edge origin makes the card rise off the table rather than
     into it, and keeps that edge put (no dead click strip; hence no translateY). */
  .mastermind-tile--tilt-lift .mastermind__card-frame {
    transform-origin: 50% 100%;
    transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
  }
  /* why: lift the card frame, not the button. The button also holds the
     "Tactics remaining" and Final Blow lines, which stay on the table, and it is
     the hover target that never moves, so the lifted card cannot flicker out from
     under the pointer. */
  .mastermind-tile--tilt-lift:not(.mastermind-tile--guessing) .mastermind__fight-button:hover .mastermind__card-frame {
    transform: rotateX(calc(-1 * var(--board-tilt-angle))) scale(1.06);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.45);
  }
  /* why: the tile's own 1.05 hover scale would compound with the lift; inside a
     lifting frame the lifted card is exactly 1.06. */
  .mastermind-tile--tilt-lift .mastermind__card-frame .card-tile--interactive:hover {
    transform: none;
  }
}
</style>
